// Э-10. Шаблоны цикла: сетка «работы × дни цикла» как в Excel заказчика, связи с лагами, проверка.
// «Цикл этажа» — через сколько дней начинается следующий этаж; по умолчанию равен дням работ (встреча 24.10.2026).

import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { navigate } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Drawer, Empty, Field, Input, Loading, Modal, Select, useAsync, useToast } from "../../shared/ui";
import { GripScheme, type Zone } from "../components";
import { api, can, useCtx } from "../ctx";

type Drag = { i: number; a: number; b: number };

/** Для чего шаблон: этаж или этап корпуса до этажей (встреча 24.10.2026). */
export const STAGE_RU: Record<string, string> = { floor: "этаж", prep: "подготовительный этап", foundation: "фундаментная плита" };
/** Группы захваток: стены (С), перекрытие (П), участок этапа (У) — земляные работы, подбетонка, гидроизоляция. */
const STAGE_ORDER = ["prep", "foundation", "floor"];
const GROUP_PREFIX: Record<string, string> = { vertical: "С", slab: "П", general: "У" };
const GROUP_LABEL: Record<string, string> = { vertical: "стены", slab: "перекрытие", general: "участок" };
const groupOrder = (code: string) => ({ С: 0, П: 1, У: 2 } as Record<string, number>)[code[0]] ?? 3;

export function TemplatesPage({ id }: { id?: string }) {
  const toast = useToast();
  // по ТЗ руководитель смотрит шаблоны, а меняет их только администратор
  const canEdit = can(useCtx().me, "templates.edit");
  const list = useAsync<any[]>(() => api.get("/cycle-templates"), []);
  const [importing, setImporting] = useState(false);
  const [creating, setCreating] = useState(false);
  const sel = id || list.data?.[0]?.id;
  const create = async (fn: () => Promise<any>, msg: string) => {
    try { const t = await fn(); toast.ok(msg); list.reload(); navigate(`/templates/${t.id}`); } catch (e) { toast.error(e); }
  };
  return (
    <>
      <TopBar title="Шаблоны циклов">
        {canEdit ? (
          <>
            <Button variant="primary" onClick={() => setCreating(true)}>Новый шаблон</Button>
            <Button onClick={() => setImporting(true)}>Загрузить из Excel</Button>
          </>
        ) : <span className="muted small">Просмотр. Изменять шаблоны может администратор.</span>}
      </TopBar>
      <div className="content">
        {!list.data ? <Loading error={list.error} onRetry={list.reload} /> : list.data.length === 0 ? <Empty>Шаблонов нет. Нажмите «Новый шаблон» или загрузите сетку из Excel.</Empty> : (
          <div style={{ display: "grid", gridTemplateColumns: "240px minmax(0,1fr)", gap: 16 }} className="split">
            <div className="panel">
              {[...list.data].sort((a, b) => STAGE_ORDER.indexOf(a.stage || "floor") - STAGE_ORDER.indexOf(b.stage || "floor")).map((t) => (
                <button key={t.id} className={`order-row${t.id === sel ? " active" : ""}`} style={{ width: "100%", textAlign: "left", border: 0, background: "none" }} onClick={() => navigate(`/templates/${t.id}`)}>
                  <div className="t1"><b>{t.name}</b></div>
                  <div className="small muted">{STAGE_RU[t.stage] || "этаж"} · {(t.stage || "floor") === "floor" ? "цикл" : "длительность"} {t.step_days} дн.{t.problems.length ? " · есть ошибки" : ""}</div>
                </button>
              ))}
            </div>
            {sel && <Editor key={sel} id={sel} canEdit={canEdit} onSaved={list.reload} onCopy={(t) => create(() => api.post(`/cycle-templates/${t.id}/copy`, { name: `${t.name} (копия)` }), "Копия создана")} />}
          </div>
        )}
      </div>
      {creating && <CreateDialog templates={list.data || []} onClose={() => setCreating(false)} onDone={(t) => { setCreating(false); toast.ok("Шаблон создан — заполните сетку работ и сохраните"); list.reload(); navigate(`/templates/${t.id}`); }} />}
      {importing && <ImportDialog onClose={() => setImporting(false)} onDone={(t) => { setImporting(false); list.reload(); navigate(`/templates/${t.id}`); }} />}
    </>
  );
}

// ------------------------------------------------------------------ сетка ↔ связи (замечание 24.10.2026, №6)
// Связь «Б после А, выдержка L» значит: Б начинается не раньше дня (начало А + длительность А + L).
// Сетка → связи: перетащили работу раньше, чем позволяет связь, — выдержка уменьшается; у «плотной» связи
// (работа шла сразу по ней) выдержка повторяет нарисованное. Связи → сетка: изменили связь, длительность или
// перетащили работу — зависящие работы сдвигаются; работы, стоявшие вплотную к связи, идут за ней и раньше.
// Связи с предыдущим этажом здесь не участвуют: они действуют между этажами при построении плана.

type GT = { key?: string; grip_code: string; work_type_code: string; start_day: any; duration: any };
type GD = { task_key: string; predecessor_key: string; lag_days: any; prev_cycle: boolean };
const keyOf = (x: GT) => x.key || `${x.grip_code}/${x.work_type_code}`;
const num = (x: any, d = 0) => (Number.isFinite(Number(x)) && String(x).trim() !== "" ? Number(x) : d);

function reqStart(tasks: GT[], deps: GD[], key: string): number | null {
  let r: number | null = null;
  for (const d of deps) {
    if (d.prev_cycle || d.task_key !== key || d.predecessor_key === key) continue;
    const p = tasks.find((t) => keyOf(t) === d.predecessor_key);
    if (!p) continue;
    const v = num(p.start_day, 1) + num(p.duration, 1) + num(d.lag_days);
    r = r == null ? v : Math.max(r, v);
  }
  return r;
}

/** Пересчёт сетки по связям. Плотные (по прежнему состоянию) работы идут за связью, остальные — не раньше неё. */
function propagate(before: { tasks: GT[]; deps: GD[] }, tasks: GT[], deps: GD[], fixed?: string): { tasks: GT[]; moved: string[] } {
  const tight = new Set(before.tasks.filter((t) => { const r = reqStart(before.tasks, before.deps, keyOf(t)); return r != null && num(t.start_day, 1) === r; }).map(keyOf));
  const out = tasks.map((t) => ({ ...t }));
  const moved = new Set<string>();
  for (let it = 0; it <= out.length; it++) {
    let changed = false;
    for (const t of out) {
      const k = keyOf(t);
      if (k === fixed) continue;
      const r = reqStart(out, deps, k);
      if (r == null) continue;
      const cur = num(t.start_day, 1);
      const next = Math.max(1, tight.has(k) ? r : Math.max(cur, r));
      if (next !== cur) { t.start_day = next; moved.add(k); changed = true; }
    }
    if (!changed) break;
  }
  return { tasks: out, moved: [...moved] };
}

/** Работу нарисовали заново (протяжка, «Старт», «Дней»): подстроить её входящие связи и сдвинуть зависящие. */
function redraw(tasks: GT[], deps: GD[], i: number, start: number, duration: number): { tasks: GT[]; deps: GD[]; moved: string[]; lags: string[] } {
  const k = keyOf(tasks[i]);
  const oldReq = reqStart(tasks, deps, k);
  const wasTight = oldReq != null && num(tasks[i].start_day, 1) === oldReq;
  const nt = tasks.map((x, j) => (j === i ? { ...x, start_day: start, duration } : x));
  const lags: string[] = [];
  const nd = deps.map((d) => {
    if (d.prev_cycle || d.task_key !== k || d.predecessor_key === k) return d;
    const p = nt.find((t) => keyOf(t) === d.predecessor_key);
    if (!p) return d;
    const end = num(p.start_day, 1) + num(p.duration, 1);
    const need = start - end;
    const lag = num(d.lag_days);
    if (lag > need || (wasTight && end + lag === oldReq && need !== lag)) {
      lags.push(`${d.predecessor_key}|${k}`);
      return { ...d, lag_days: need };
    }
    return d;
  });
  const res = propagate({ tasks, deps }, nt, nd, k);
  return { tasks: res.tasks, deps: nd, moved: res.moved, lags };
}

function Editor({ id, canEdit, onSaved, onCopy }: { id: string; canEdit: boolean; onSaved: () => void; onCopy: (t: any) => void }) {
  const toast = useToast();
  const t = useAsync<any>(() => api.get(`/cycle-templates/${id}`), [id]);
  const wt = useAsync<any[]>(() => api.get("/work-types"), []);
  const crews = useAsync<any[]>(() => api.get("/crews"), []);
  const [v, setV] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [scheme, setScheme] = useState<string | null>(null); // код захватки, схема которой открыта
  const [add, setAdd] = useState({ grip: "", work: "" });
  // ФТ-2.1: протяжка по ячейкам строки — нажать на первый день и вести до последнего
  const dragRef = useRef<Drag | null>(null);
  const [drag, setDragState] = useState<Drag | null>(null);
  const setDrag = (d: Drag | null) => { dragRef.current = d; setDragState(d); };
  useEffect(() => { if (t.data) setV(structuredClone(t.data)); }, [t.data]);
  const vRef = useRef<any>(null); // последнее состояние сетки — для окончания протяжки
  vRef.current = v;
  useEffect(() => {
    if (!drag) return;
    const move = (e: PointerEvent) => {
      const el = (document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null)?.closest<HTMLElement>("[data-day]");
      const d = dragRef.current;
      if (d && el && Number(el.dataset.row) === d.i && Number(el.dataset.day) !== d.b) setDrag({ ...d, b: Number(el.dataset.day) });
    };
    const up = () => {
      const d = dragRef.current;
      setDrag(null);
      if (!d) return;
      const start = Math.min(d.a, d.b);
      if (vRef.current) setV(applyRedraw(vRef.current, d.i, start, Math.abs(d.b - d.a) + 1));
    };
    const cancel = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", cancel); };
  }, [drag !== null]); // eslint-disable-line react-hooks/exhaustive-deps
  // сетка и связи меняются вместе; что подстроилось — коротко в сообщении
  const tell = (cur: any, moved: string[], lags: string[]) => {
    const name = (k: string) => { const [g, w] = k.split("/"); const t = cur.tasks.find((x: any) => keyOf(x) === k); return `${g} · ${t?.work_type || w}`; };
    const parts = [];
    if (lags.length) parts.push(`выдержка изменена: ${lags.map((x) => x.split("|").map(name).join(" → ")).join(", ")}`);
    if (moved.length) parts.push(`сдвинуты: ${moved.map(name).join(", ")}`);
    if (parts.length) toast.ok(`Сетка и связи подстроены — ${parts.join("; ")}`);
  };
  function applyRedraw(cur: any, i: number, start: number, duration: number) {
    const r = redraw(cur.tasks, cur.dependencies, i, start, duration);
    tell(cur, r.moved, r.lags);
    return { ...cur, tasks: r.tasks, dependencies: r.deps };
  }
  function applyDeps(cur: any, deps: any[]) {
    const r = propagate({ tasks: cur.tasks, deps: cur.dependencies }, cur.tasks, deps);
    tell(cur, r.moved, []);
    return { ...cur, tasks: r.tasks, dependencies: deps };
  }
  const span = useMemo(() => (v ? Math.max(1, ...v.tasks.map((x: any) => Number(x.start_day) + Number(x.duration) - 1)) : 0), [v]);
  if (!v) return <Loading error={t.error} onRetry={t.reload} />;
  const cycle = Number(v.step_days) || span;
  const cols = Math.min(60, Math.max(span, cycle) + 3); // запас справа — чтобы протянуть работу дальше
  const crewOpts = [...new Set([...(crews.data || []).map((c) => c.code), ...v.tasks.map((x: any) => x.crew_code).filter(Boolean)])].map((c) => ({ value: c, label: c }));
  const setGrip = (code: string, p: any) => setV({ ...v, grips: v.grips.map((g: any) => (g.code === code ? { ...g, ...p } : g)) });
  const addGrip = (group: "vertical" | "slab" | "general") => {
    const prefix = GROUP_PREFIX[group];
    const nums = v.grips.filter((g: any) => g.group === group).map((g: any) => g.number);
    const n = (nums.length ? Math.max(...nums) : 0) + 1;
    setV({ ...v, grips: [...v.grips, { code: `${prefix}${n}`, number: n, group, over_grips: null, note: null, scheme_file_id: null, zone: null }] });
  };
  const removeGrip = (code: string) => {
    if (v.tasks.some((x: any) => x.grip_code === code)) { toast.error(`Сначала уберите работы захватки ${code} из сетки`); return; }
    setV({ ...v, grips: v.grips.filter((g: any) => g.code !== code) });
  };
  const removeTask = (i: number) => {
    const key = keys[i];
    setV({ ...v, tasks: v.tasks.filter((_: any, k: number) => k !== i), dependencies: v.dependencies.filter((d: any) => d.task_key !== key && d.predecessor_key !== key), final_task_key: v.final_task_key === key ? null : v.final_task_key });
  };
  const addTask = () => {
    const w = wt.data?.find((x) => x.code === add.work);
    if (!add.grip || !w) return;
    const key = `${add.grip}/${w.code}`;
    if (keys.includes(key)) { toast.error(`«${w.short_name}» на захватке ${add.grip} уже есть в сетке`); return; }
    setV({ ...v, tasks: [...v.tasks, { key, grip_code: add.grip, work_type_code: w.code, work_type: w.short_name, crew_code: w.default_crew_code, start_day: 1, duration: 1, planned_volume: null, planned_headcount: null, is_concreting: w.is_concreting }] });
    setAdd({ grip: add.grip, work: "" });
    toast.ok("Строка добавлена — протяните по дням, когда идёт работа");
  };
  const dirty = JSON.stringify(v) !== JSON.stringify(t.data);
  const setTask = (i: number, p: any) => setV({ ...v, tasks: v.tasks.map((x: any, k: number) => (k === i ? { ...x, ...p } : x)) });
  const setDep = (i: number, p: any) => setV(applyDeps(v, v.dependencies.map((x: any, k: number) => (k === i ? { ...x, ...p } : x))));
  const keys = v.tasks.map((x: any) => x.key || `${x.grip_code}/${x.work_type_code}`);
  const workName = (code: string) => wt.data?.find((w) => w.code === code)?.short_name || code;
  const keyLabel = (k: string) => { const [g, w] = k.split("/"); return `${g} · ${workName(w)}`; };
  const labelize = (text: string) => text.replace(/([СПУ]\d+)\/([a-z0-9_]+)/g, (_m, g, w) => `${g} · ${workName(w)}`);
  const winter = t.data.problems_winter.filter((p: string) => !t.data.problems.includes(p));
  const keyOpts = keys.map((k: string) => ({ value: k, label: keyLabel(k) }));
  const tasks = v.tasks.map((x: any, i: number) => ({ ...x, i })).sort((a: any, b: any) => (a.grip_code[0] === b.grip_code[0] ? a.start_day - b.start_day : groupOrder(a.grip_code) - groupOrder(b.grip_code)));
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        name: v.name, project_id: v.project_id, stage: v.stage, step_days: Number(v.step_days) || span, planned_duration: span, final_task_key: v.final_task_key,
        grips: v.grips, tasks: v.tasks.map((x: any) => ({ ...x, key: x.key || `${x.grip_code}/${x.work_type_code}`, start_day: Number(x.start_day), duration: Number(x.duration), planned_volume: x.planned_volume === "" ? null : x.planned_volume, planned_headcount: x.planned_headcount === "" ? null : x.planned_headcount })),
        dependencies: v.dependencies.map((d: any) => ({ ...d, lag_days: Number(d.lag_days) || 0, winter_lag_days: d.winter_lag_days === "" || d.winter_lag_days == null ? null : Number(d.winter_lag_days) })),
      };
      const r = await api.put(`/cycle-templates/${id}`, body);
      t.set(r);
      onSaved();
      toast.ok(r.problems.length ? "Сохранено, но в шаблоне есть ошибки" : "Шаблон сохранён");
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  return (
    // для просмотра все поля и кнопки сетки неактивны — сохранить всё равно может только администратор
    <fieldset className="stack" disabled={!canEdit} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div className="panel pad stack">
        <div className="form-grid">
          <Field label="Название" wide><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} /></Field>
          <Field label={v.stage === "floor" ? "Цикл этажа, дн." : "Длительность этапа, дн."}
            hint={<>
              {v.stage === "floor" ? "Через сколько дней после начала этажа начинается следующий. " : "Через сколько дней после начала этапа начинается следующий. "}
              {span > 0 && (cycle === span
                ? `Совпадает с сеткой ниже: работы идут с 1-го по ${span}-й день.`
                : <>По сетке ниже работы идут {span} дн. — {cycle < span ? `следующий начнётся раньше, внахлёст на ${span - cycle} дн.` : `после них ${cycle - span} дн. без работ.`}{" "}
                  <button type="button" className="link" onClick={() => setV({ ...v, step_days: String(span) })}>Сделать {span} дн.</button></>)}
            </>}>
            <Input inputMode="numeric" value={v.step_days} onChange={(e) => setV({ ...v, step_days: e.target.value.replace(/\D/g, "") })} />
          </Field>
          <Field label="Для чего шаблон"><Select value={v.stage} onChange={(e) => setV({ ...v, stage: e.target.value })} options={Object.entries(STAGE_RU).map(([value, label]) => ({ value, label }))} /></Field>
          <Field label="Завершающая работа" wide><Select value={v.final_task_key || ""} onChange={(e) => setV({ ...v, final_task_key: e.target.value })} options={keyOpts} /></Field>
        </div>
        {t.data.problems.length > 0 && (
          <div className="alert risk"><div className="t">В шаблоне есть ошибки — план по нему не построить:</div>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>{t.data.problems.map((p: string) => <li key={p}>{labelize(p)}</li>)}</ul>
          </div>
        )}
        {winter.length > 0 && (
          <div className="alert info"><div className="t">Зимой из-за выдержки бетона работы сдвинутся (план пересчитается сам):</div>
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>{winter.map((p: string) => <li key={p}>{labelize(p)}</li>)}</ul>
          </div>
        )}
        <div className="row">
          {canEdit && (
            <>
              <Button variant="primary" disabled={!dirty || busy} onClick={save}>Сохранить</Button>
              <Button disabled={!dirty} onClick={() => setV(structuredClone(t.data))}>Отменить правки</Button>
              <Button variant="ghost" onClick={() => onCopy(t.data)}>Копировать</Button>
            </>
          )}
          <span className="muted small grow right">Захватки: {v.grips.map((g: any) => g.code + (g.over_grips ? ` (над ${g.over_grips.join(", ")})` : "")).join(", ")}</span>
        </div>
      </div>

      <div className="panel">
        <div className="ph"><h2>Работы по дням цикла</h2><span className="muted small">{canEdit ? "Нажмите на день начала и протяните до последнего дня работы" : "Сетка работ по дням цикла"}</span></div>
        <div className="pb gantt tpl-grid">
          <table>
            <thead><tr>
              <th style={{ textAlign: "left", padding: "0 10px" }}>Захватка · работа</th><th className="small muted" title="Кто делает: специальность">Спец.</th><th className="small muted">Старт</th><th className="small muted">Дней</th><th className="small muted">Объём</th>
              {Array.from({ length: cols }, (_, d) => <th key={d} className="day">{d + 1}</th>)}
              {canEdit && <th />}
            </tr></thead>
            <tbody>{tasks.map((x: any, n: number) => {
              const sep = n > 0 && tasks[n - 1].grip_code[0] !== x.grip_code[0];
              return (
                <tr key={x.i} className={sep ? "sep" : ""}>
                  <td className="name">{x.grip_code} · {x.work_type || wt.data?.find((w) => w.code === x.work_type_code)?.short_name}</td>
                  <td className="crew"><select className="select" aria-label="Специальность" value={x.crew_code || ""} onChange={(e) => setTask(x.i, { crew_code: e.target.value || null })}>
                    <option value="">—</option>{crewOpts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select></td>
                  <td><input className="input" aria-label="День начала" value={x.start_day} onChange={(e) => setTask(x.i, { start_day: e.target.value })}
                    onBlur={(e) => { const n = Number(e.target.value); if (Number.isInteger(n) && n >= 1) setV(applyRedraw(v, x.i, n, num(x.duration, 1))); }} /></td>
                  <td><input className="input" aria-label="Длительность" value={x.duration} onChange={(e) => setTask(x.i, { duration: e.target.value })}
                    onBlur={(e) => { const n = Number(e.target.value); if (Number.isInteger(n) && n >= 1) setV(applyRedraw(v, x.i, num(x.start_day, 1), n)); }} /></td>
                  <td><input className="input" aria-label="Объём" value={x.planned_volume ?? ""} onChange={(e) => setTask(x.i, { planned_volume: e.target.value === "" ? "" : Number(e.target.value.replace(",", ".")) })} /></td>
                  {Array.from({ length: cols }, (_, d) => {
                    const day = d + 1;
                    const dr = drag && drag.i === x.i ? drag : null;
                    const on = dr ? day >= Math.min(dr.a, dr.b) && day <= Math.max(dr.a, dr.b) : day >= Number(x.start_day) && day < Number(x.start_day) + Number(x.duration);
                    return (
                      <td key={d} data-row={x.i} data-day={day} className={`d paint${day > cycle ? " off" : ""}`}
                        onPointerDown={canEdit ? (e) => { e.preventDefault(); setDrag({ i: x.i, a: day, b: day }); } : undefined}>
                        {on && <div className={`blk ${dr ? "drag" : x.is_concreting ? "in_progress" : "plan"}`} />}
                      </td>
                    );
                  })}
                  {canEdit && <td><button type="button" className="row-x" title="Убрать строку" aria-label={`Убрать ${x.grip_code} · ${x.work_type}`} onClick={() => removeTask(x.i)}>×</button></td>}
                </tr>
              );
            })}</tbody>
          </table>
        </div>
        {canEdit && (
          <div className="pb row tight">
            <span className="small">Добавить строку:</span>
            <Select aria-label="Захватка новой строки" style={{ width: 110 }} value={add.grip} onChange={(e) => setAdd({ ...add, grip: e.target.value })} placeholder="захватка" options={v.grips.map((g: any) => ({ value: g.code, label: g.code }))} />
            <Select aria-label="Вид работ новой строки" style={{ width: 260 }} value={add.work} onChange={(e) => setAdd({ ...add, work: e.target.value })} placeholder="вид работ"
              options={(wt.data || []).filter((w) => { const g = v.grips.find((x: any) => x.code === add.grip); return !g || w.group === g.group; }).map((w) => ({ value: w.code, label: w.short_name }))} />
            <Button size="small" disabled={!add.grip || !add.work} onClick={addTask}>Добавить</Button>
          </div>
        )}
        <div className="pb small muted">Серым — дни после цикла этажа: в это время уже идёт следующий этаж. Синим — бетонирование. После правок нажмите «Сохранить». Чтобы правки попали в план дома — Администрирование → «Перестроить план» у дома.</div>
      </div>

      <div className="panel">
        <div className="ph"><h2>Захватки · {v.grips.length}</h2>
          {canEdit && <div className="row tight"><Button size="small" onClick={() => addGrip("vertical")}>+ захватка стен</Button><Button size="small" onClick={() => addGrip("slab")}>+ захватка перекрытия</Button><Button size="small" onClick={() => addGrip("general")}>+ участок этапа</Button></div>}
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Код</th><th>Группа</th><th>Над захватками стен</th><th>Примечание</th><th>Схема</th><th /></tr></thead>
            <tbody>{v.grips.map((g: any) => (
              <tr key={g.code}>
                <td><b>{g.code}</b></td>
                <td>{GROUP_LABEL[g.group] || g.group}</td>
                <td>{g.group === "slab"
                  ? <Input aria-label={`Над какими захватками ${g.code}`} style={{ width: 140 }} placeholder="С1, С2" value={(g.over_grips || []).join(", ")}
                      onChange={(e) => setGrip(g.code, { over_grips: e.target.value.split(/[,\s]+/).map((x) => x.trim().toUpperCase()).filter(Boolean) })} />
                  : <span className="muted">—</span>}</td>
                <td><Input aria-label={`Примечание ${g.code}`} value={g.note || ""} onChange={(e) => setGrip(g.code, { note: e.target.value || null })} /></td>
                <td>
                  <Button size="small" onClick={() => setScheme(g.code)}>{g.scheme_file_id ? (g.zone ? "Схема ✓" : "Схема: выделите зону") : "Загрузить схему"}</Button>
                </td>
                <td>{canEdit && <Button size="small" variant="ghost" onClick={() => removeGrip(g.code)}>Убрать</Button>}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div className="pb small muted">Схема — изображение плана этажа (JPEG, PNG), на котором выделена зона захватки. Её видно в шахматке при нажатии на ячейку.</div>
      </div>

      <div className="panel">
        <div className="ph"><h2>Связи между работами · {v.dependencies.length}</h2>
          <Button size="small" onClick={() => setV({ ...v, dependencies: [...v.dependencies, { task_key: keys[0], predecessor_key: keys[0], lag_days: 0, winter_lag_days: null, prev_cycle: false }] })}>Добавить связь</Button>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Работа</th><th>Начинается после</th><th className="num">Лаг, дн.</th><th className="num">Зимой, дн.</th><th>Предыдущего этажа</th><th /></tr></thead>
            <tbody>{v.dependencies.map((d: any, i: number) => (
              <tr key={i}>
                <td><Select aria-label="Работа" value={d.task_key} onChange={(e) => setDep(i, { task_key: e.target.value })} options={keyOpts} /></td>
                <td><Select aria-label="Предшественник" value={d.predecessor_key} onChange={(e) => setDep(i, { predecessor_key: e.target.value })} options={keyOpts} /></td>
                <td className="num"><Input aria-label="Лаг" style={{ width: 70 }} value={d.lag_days} onChange={(e) => setDep(i, { lag_days: e.target.value })} /></td>
                <td className="num"><Input aria-label="Лаг зимой" style={{ width: 70 }} value={d.winter_lag_days ?? ""} placeholder="как летом" onChange={(e) => setDep(i, { winter_lag_days: e.target.value })} /></td>
                <td><label className="check"><input type="checkbox" checked={d.prev_cycle} onChange={(e) => setDep(i, { prev_cycle: e.target.checked })} /> да</label></td>
                <td><Button size="small" variant="ghost" onClick={() => setV(applyDeps(v, v.dependencies.filter((_: any, k: number) => k !== i)))}>Убрать</Button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div className="pb small muted">Лаг — выдержка после окончания предыдущей работы (набор прочности, демонтаж): 0 — на следующий день, отрицательный — внахлёст. Сетка и связи связаны: протянули работу в сетке — подстроится выдержка и сдвинутся зависящие работы; изменили связь — сдвинутся работы в сетке. «Предыдущего этажа» — связь с работой этажа ниже, в сетке этажа не учитывается.</div>
      </div>
      {scheme && (
        <SchemeEditor grip={v.grips.find((g: any) => g.code === scheme)} grips={v.grips} canEdit={canEdit} onClose={() => setScheme(null)}
          onApply={(patch) => { setV({ ...v, grips: v.grips.map((g: any) => (patch[g.code] ? { ...g, ...patch[g.code] } : g)) }); setScheme(null); toast.ok("Схема применена — сохраните шаблон"); }} />
      )}
    </fieldset>
  );
}

/** Новый шаблон (встреча 24.10.2026: кнопка «Типовой по графику заказчика» была непонятна). */
function CreateDialog({ templates, onClose, onDone }: { templates: any[]; onClose: () => void; onDone: (t: any) => void }) {
  const toast = useToast();
  const [v, setV] = useState({ name: "", stage: "floor", from: "standard", step: "" });
  const sameStage = templates.filter((t) => t.stage === v.stage);
  const create = async () => {
    const name = v.name.trim();
    try {
      let t;
      if (v.from === "empty") {
        const g = v.stage === "prep" ? { code: "У1", number: 1, group: "general" } : v.stage === "foundation" ? { code: "П1", number: 1, group: "slab" } : { code: "С1", number: 1, group: "vertical" };
        t = await api.post("/cycle-templates", { name, stage: v.stage, step_days: v.step ? Number(v.step) : null, grips: [g], tasks: [], dependencies: [] });
      } else if (v.from === "standard") {
        t = await api.post("/cycle-templates/standard", { name, stage: v.stage });
      } else {
        t = await api.post(`/cycle-templates/${v.from}/copy`, { name });
      }
      onDone(t);
    } catch (e) { toast.error(e); }
  };
  const fromOpts = [
    { value: "standard", label: v.stage === "floor" ? "Типовой этаж по графику заказчика (5 захваток стен, 2 перекрытия)" : v.stage === "prep" ? "Типовой: земляные работы, подбетонка, гидроизоляция, стяжка" : "Типовая: опалубка, армирование, бетонирование плиты" },
    { value: "empty", label: "Пустая сетка — заполню сам" },
    ...sameStage.map((t) => ({ value: t.id, label: `Копия «${t.name}»` })),
  ];
  return (
    <Modal title="Новый шаблон" onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={v.name.trim().length < 2} onClick={create}>Создать</Button>
    </>}>
      <div className="form-grid">
        <Field label="Название" wide><Input autoFocus value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} placeholder={v.stage === "floor" ? "Например: Типовой этаж, корпус 2 (этажи 2–10)" : v.stage === "prep" ? "Например: Подготовительный этап, корпус 2" : "Например: Фундаментная плита, корпус 2"} /></Field>
        <Field label="Для чего" wide><Select value={v.stage} onChange={(e) => setV({ ...v, stage: e.target.value, from: "standard" })} options={[
          { value: "floor", label: "Этаж — типовой, подземный, первый, технический" }, { value: "prep", label: "Подготовительный этап" }, { value: "foundation", label: "Фундаментная плита" },
        ]} /></Field>
        <Field label="С чего начать" wide><Select value={v.from} onChange={(e) => setV({ ...v, from: e.target.value })} options={fromOpts} /></Field>
        {v.from === "empty" && <Field label={v.stage === "floor" ? "Цикл этажа, дн." : "Длительность этапа, дн."} hint="Можно позже; пусто — по дням работ"><Input inputMode="numeric" value={v.step} onChange={(e) => setV({ ...v, step: e.target.value.replace(/\D/g, "") })} /></Field>}
      </div>
      <p className="small muted">После создания откроется сетка «Работы по дням цикла»: добавьте захватки и работы, протяните дни, задайте связи и сохраните.</p>
    </Modal>
  );
}

function ImportDialog({ onClose, onDone }: { onClose: () => void; onDone: (t: any) => void }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [step, setStep] = useState("");
  const [file, setFile] = useState<File | null>(null);
  return (
    <Modal title="Шаблон из Excel" onClose={onClose} actions={<>
      <Button onClick={onClose}>Отмена</Button>
      <Button variant="primary" disabled={!file || !name.trim()} onClick={async () => {
        const fd = new FormData();
        fd.append("name", name.trim()); if (step) fd.append("step_days", step); fd.append("file", file!);
        try { onDone(await api.post("/cycle-templates/import", fd)); } catch (e) { toast.error(e); }
      }}>Загрузить</Button>
    </>}>
      <div className="form-grid">
        <Field label="Название" wide><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Цикл этажа, дн." hint="Пусто — по дням работ в файле"><Input inputMode="numeric" value={step} onChange={(e) => setStep(e.target.value.replace(/\D/g, ""))} /></Field>
        <Field label="Файл .xlsx" hint="Строки — захватка и работа, столбцы — дни цикла, отмеченные ячейки — дни работы"><input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} /></Field>
      </div>
    </Modal>
  );
}

/** ФТ-2.2. Схема захватки: загрузить план этажа (или взять у другой захватки) и выделить её зону — рамкой
 * или контуром по точкам любой формы, например буквой Г (встреча 24.10.2026). */
function SchemeEditor({ grip, grips, canEdit, onClose, onApply }: {
  grip: any; grips: any[]; canEdit: boolean; onClose: () => void; onApply: (patch: Record<string, { scheme_file_id: string | null; zone: Zone | null }>) => void;
}) {
  const toast = useToast();
  const [fileId, setFileId] = useState<string | null>(grip.scheme_file_id);
  const [zone, setZone] = useState<Zone | null>(grip.zone);
  const [mode, setMode] = useState<"rect" | "poly">(grip.zone?.points ? "poly" : "rect");
  const [pts, setPts] = useState<[number, number][]>(grip.zone?.points || []);
  const [closed, setClosed] = useState<boolean>(!!grip.zone?.points);
  const [all, setAll] = useState(true);
  const [busy, setBusy] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const dragPt = useRef<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const sources = [...new Map(grips.filter((g) => g.scheme_file_id && g.code !== grip.code).map((g) => [g.scheme_file_id, g.code])).entries()];
  const others = grips.filter((g) => g.code !== grip.code && g.group === grip.group && g.zone && g.scheme_file_id === fileId).map((g) => ({ code: g.code, zone: g.zone }));
  const upload = async (f: File | undefined) => {
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Нужно изображение: JPEG или PNG"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Файл больше 8 МБ — уменьшите изображение"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try { const r = await api.post("/files", fd); setFileId(r.id); reset(); } catch (e) { toast.error(e); } finally { setBusy(false); }
  };
  const at = (e: React.PointerEvent) => {
    const r = box.current!.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  const rect = (a: { x: number; y: number }, b: { x: number; y: number }): Zone => ({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y) });
  const r4 = (n: number) => Math.round(n * 10000) / 10000;
  const polyZone = (p: [number, number][]): Zone => {
    const xs = p.map((q) => q[0]), ys = p.map((q) => q[1]);
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys), points: p.map(([x, y]) => [r4(x), r4(y)]) };
  };
  function reset() { setZone(null); setPts([]); setClosed(false); }
  const close = (p = pts) => { if (p.length >= 3) { setClosed(true); setZone(polyZone(p)); } };
  // контур: нажатие — новая точка; нажатие на первую точку (или «Замкнуть») — контур готов; точки можно тянуть
  const onDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const q = at(e);
    if (mode === "rect") { start.current = q; setZone({ ...q, w: 0, h: 0 }); return; }
    const r = box.current!.getBoundingClientRect();
    const near = pts.findIndex(([x, y]) => Math.hypot((x - q.x) * r.width, (y - q.y) * r.height) < 12);
    if (near >= 0) {
      if (!closed && near === 0 && pts.length >= 3) { close(); return; }
      dragPt.current = near;
      return;
    }
    if (!closed) setPts([...pts, [q.x, q.y]]);
  };
  const onMove = (e: React.PointerEvent) => {
    if (mode === "rect") { if (start.current) setZone(rect(start.current, at(e))); return; }
    if (dragPt.current == null) return;
    const q = at(e);
    const next = pts.map((p, i) => (i === dragPt.current ? [q.x, q.y] : p)) as [number, number][];
    setPts(next);
    if (closed) setZone(polyZone(next));
  };
  const onUp = (e: React.PointerEvent) => {
    if (mode === "rect") {
      if (!start.current) return;
      const z = rect(start.current, at(e));
      start.current = null;
      setZone(z.w > 0.01 && z.h > 0.01 ? z : null);
      return;
    }
    dragPt.current = null;
  };
  const apply = () => {
    const patch: Record<string, { scheme_file_id: string | null; zone: Zone | null }> = { [grip.code]: { scheme_file_id: fileId, zone: fileId ? zone : null } };
    if (all && fileId) for (const g of grips) if (g.code !== grip.code && !g.scheme_file_id) patch[g.code] = { scheme_file_id: fileId, zone: null };
    onApply(patch);
  };
  const shown: Zone | null = mode === "poly" && !closed ? null : zone;
  return (
    <Drawer title={`Схема захватки ${grip.code}`} onClose={onClose} footer={canEdit ? <>
      <Button variant="primary" disabled={busy || (!!fileId && !zone)} onClick={apply}>Применить</Button>
      {grip.scheme_file_id && <Button variant="ghost" onClick={() => onApply({ [grip.code]: { scheme_file_id: null, zone: null } })}>Убрать схему</Button>}
      <Button onClick={onClose}>Отмена</Button>
    </> : undefined}>
      <div className="stack">
        {canEdit && (
          <div className="row tight">
            <label className={`btn small${busy ? " disabled" : ""}`}>{fileId ? "Другой план этажа" : "Загрузить план этажа"}
              <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {sources.length > 0 && (
              <Select aria-label="Взять план у захватки" style={{ width: 230 }} value="" placeholder="или взять план у захватки…"
                onChange={(e) => { if (e.target.value) { setFileId(e.target.value); reset(); } }}
                options={sources.map(([id, code]) => ({ value: id, label: `как у ${code}` }))} />
            )}
          </div>
        )}
        {!fileId ? <Empty>Плана этажа для этой захватки нет.{canEdit ? " Загрузите изображение плана (скан или выгрузку из чертежа)." : ""}</Empty> : (
          <>
            {canEdit && (
              <div className="row tight">
                <span className="small">Как выделить:</span>
                <Button size="small" aria-pressed={mode === "rect"} variant={mode === "rect" ? "primary" : undefined} onClick={() => { setMode("rect"); reset(); }}>Рамкой</Button>
                <Button size="small" aria-pressed={mode === "poly"} variant={mode === "poly" ? "primary" : undefined} onClick={() => { setMode("poly"); reset(); }}>По точкам (любая форма)</Button>
              </div>
            )}
            {canEdit && <div className="small">{mode === "rect"
              ? (zone ? "Зона выделена. Чтобы выделить заново — обведите рамкой ещё раз." : "Обведите рамкой зону захватки: нажмите в одном углу и протяните до противоположного.")
              : closed ? "Контур готов. Точки можно перетащить; «Начать заново» — нарисовать снова."
                : `Нажимайте по углам захватки по порядку${pts.length ? ` (точек: ${pts.length})` : ""}. Чтобы замкнуть — нажмите на первую точку или «Замкнуть контур».`}</div>}
            <GripScheme url={api.base + `/files/${fileId}?token=${api.token}`} zone={shown} label={grip.code} others={others}>
              {canEdit && (
                <div ref={box} className="scheme-draw" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}>
                  {mode === "poly" && pts.length > 0 && !closed && (
                    <svg viewBox="0 0 1 1" preserveAspectRatio="none"><polyline points={pts.map(([x, y]) => `${x},${y}`).join(" ")} vectorEffect="non-scaling-stroke" /></svg>
                  )}
                  {mode === "poly" && pts.map(([x, y], i) => <span key={i} className={`pt${i === 0 ? " first" : ""}`} style={{ left: `${x * 100}%`, top: `${y * 100}%` }} />)}
                </div>
              )}
            </GripScheme>
            {canEdit && mode === "poly" && (
              <div className="row tight">
                {!closed && <Button size="small" disabled={pts.length < 3} onClick={() => close()}>Замкнуть контур</Button>}
                {!closed && <Button size="small" disabled={!pts.length} onClick={() => setPts(pts.slice(0, -1))}>Убрать последнюю точку</Button>}
                <Button size="small" variant="ghost" disabled={!pts.length} onClick={reset}>Начать заново</Button>
              </div>
            )}
            {canEdit && !grip.scheme_file_id && grips.some((g) => g.code !== grip.code && !g.scheme_file_id) && (
              <label className="check small"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Тот же план — для остальных захваток без схемы (зоны выделите у каждой)</label>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
